import { twMerge } from 'tailwind-merge';
import {
  getCellBreakpointValues,
  getCellVisibillityClasses,
  getCellVisibilityStatus,
  type ResponsiveBooleans,
  type ResponsiveSources,
} from './power-cell-getters';
import { ResponsiveImage } from './responsive-image';

const CELL_BADGE_POSITION = {
  TOP_LEFT: 'top_left',
  TOP_RIGHT: 'top_right',
  BOTTOM_LEFT: 'bottom_left',
  BOTTOM_RIGHT: 'bottom_right',
  CENTER: 'center',
} as const;

/**
 * Renders the cell badge, swapping in hover / selected / selected&hover assets.
 * @param assets - Responsive cell assets
 * @param isHoverBadge - Whether hover badge swapping is active
 * @param isPreferenceSelectorCellAndSelected - Whether the cell is selected
 */
export function CellBadge({
  assets,
  isHoverBadge,
  isPreferenceSelectorCellAndSelected = false,
}: {
  assets: any;
  isHoverBadge: boolean;
  isPreferenceSelectorCellAndSelected?: boolean;
}): JSX.Element {
  const cellBadgeData: CellBadgeData = getCellBadgeData(assets);

  let {
    cellBadgeSwitches,
    cellBadgeImageSources,
    cellHoverBadgeSwitches,
    cellHoverBadgeImageSources,
    cellSelectedBadgeSwitches,
    cellSelectedBadgeImageSources,
    cellSelectedAndHoverBadgeSwitches,
    cellSelectedAndHoverBadgeImageSources,
    componentVisibilityClasses,
  } = cellBadgeData;

  const isVisible = getCellVisibilityStatus(componentVisibilityClasses);

  if (!isVisible) {
    return <></>;
  }

  const shouldUseSelectedBadge: boolean =
    isPreferenceSelectorCellAndSelected &&
    getCellVisibilityStatus(cellSelectedBadgeSwitches);
  const shouldUseSelectedAndHoverBadge: boolean =
    isPreferenceSelectorCellAndSelected &&
    isHoverBadge &&
    getCellVisibilityStatus(cellSelectedAndHoverBadgeSwitches);

  if (!isHoverBadge) {
    cellHoverBadgeSwitches = {
      mobile: false,
      tablet: false,
      desktop: false,
      'large-desktop': false,
    };
    cellSelectedAndHoverBadgeSwitches = {
      mobile: false,
      tablet: false,
      desktop: false,
      'large-desktop': false,
    };
  }

  const wrapperClassName: string = getCellBadgeWrapperClassName({
    assets,
    switches: componentVisibilityClasses,
  });

  if (shouldUseSelectedBadge || shouldUseSelectedAndHoverBadge) {
    return (
      <div className={wrapperClassName}>
        {shouldUseSelectedBadge && (
          <ResponsiveImage
            imageSources={cellSelectedBadgeImageSources}
            altText={'cell selected badge'}
            classNames={`z-[3] w-cell-badge-width h-cell-badge-height block ${getCellHoverBadgeHiddenGroupClasses(
              cellSelectedAndHoverBadgeSwitches
            )} ${getCellVisibillityClasses(cellSelectedBadgeSwitches)}`}
          />
        )}
        {shouldUseSelectedAndHoverBadge && (
          <ResponsiveImage
            imageSources={cellSelectedAndHoverBadgeImageSources}
            altText={'cell selected and hover badge'}
            classNames={`z-[3] w-cell-badge-width h-cell-badge-height hidden ${getCellHoverBadgeBlockGroupClasses(
              cellSelectedAndHoverBadgeSwitches
            )} ${getCellVisibillityClasses(cellSelectedAndHoverBadgeSwitches)}`}
          />
        )}
        {!shouldUseSelectedBadge && (
          <ResponsiveImage
            imageSources={cellBadgeImageSources}
            altText={'cell badge'}
            classNames={`z-[3] w-cell-badge-width h-cell-badge-height block ${getCellHoverBadgeHiddenGroupClasses(
              cellSelectedAndHoverBadgeSwitches
            )} ${getCellVisibillityClasses(cellBadgeSwitches)}`}
          />
        )}
      </div>
    );
  }

  return (
    <div className={wrapperClassName}>
      {getCellVisibilityStatus(cellBadgeSwitches) && (
        <ResponsiveImage
          imageSources={cellBadgeImageSources}
          altText={'cell badge'}
          classNames={`z-[3] w-cell-badge-width h-cell-badge-height block ${getCellHoverBadgeHiddenGroupClasses(
            cellHoverBadgeSwitches
          )} ${getCellVisibillityClasses(cellBadgeSwitches)}`}
        />
      )}
      {getCellVisibilityStatus(cellHoverBadgeSwitches) && (
        <ResponsiveImage
          imageSources={cellHoverBadgeImageSources}
          altText={'cell hover badge'}
          classNames={`z-[3] w-cell-badge-width h-cell-badge-height hidden ${getCellHoverBadgeBlockGroupClasses(
            cellHoverBadgeSwitches
          )} ${getCellVisibillityClasses(cellHoverBadgeSwitches)}`}
        />
      )}
    </div>
  );
}

/**
 * Overlay classes for Cell Badging Position.
 * Corners/center sit absolutely on the cell; `cell_right` stays in flow (PC2 default).
 */
export function getCellBadgePositionClasses({
  assets,
  switches,
}: {
  assets: any;
  switches: ResponsiveBooleans;
}): string {
  const keys = getCellBreakpointValues({
    config: assets,
    key: 'cell_badge_position',
    prevBreakpointFallback: true,
  });

  return twMerge(
    switches.mobile &&
      keys.mobile === CELL_BADGE_POSITION.TOP_LEFT &&
      'mobile:max-tablet:absolute mobile:max-tablet:top-0 mobile:max-tablet:left-0',
    switches.tablet &&
      keys.tablet === CELL_BADGE_POSITION.TOP_LEFT &&
      'tablet:max-desktop:absolute tablet:max-desktop:top-0 tablet:max-desktop:left-0',
    switches.desktop &&
      keys.desktop === CELL_BADGE_POSITION.TOP_LEFT &&
      'desktop:max-large-desktop:absolute desktop:max-large-desktop:top-0 desktop:max-large-desktop:left-0',
    switches['large-desktop'] &&
      keys['large-desktop'] === CELL_BADGE_POSITION.TOP_LEFT &&
      'large-desktop:absolute large-desktop:top-0 large-desktop:left-0',
    switches.mobile &&
      keys.mobile === CELL_BADGE_POSITION.TOP_RIGHT &&
      'mobile:max-tablet:absolute mobile:max-tablet:top-0 mobile:max-tablet:right-0',
    switches.tablet &&
      keys.tablet === CELL_BADGE_POSITION.TOP_RIGHT &&
      'tablet:max-desktop:absolute tablet:max-desktop:top-0 tablet:max-desktop:right-0',
    switches.desktop &&
      keys.desktop === CELL_BADGE_POSITION.TOP_RIGHT &&
      'desktop:max-large-desktop:absolute desktop:max-large-desktop:top-0 desktop:max-large-desktop:right-0',
    switches['large-desktop'] &&
      keys['large-desktop'] === CELL_BADGE_POSITION.TOP_RIGHT &&
      'large-desktop:absolute large-desktop:top-0 large-desktop:right-0',
    switches.mobile &&
      keys.mobile === CELL_BADGE_POSITION.BOTTOM_LEFT &&
      'mobile:max-tablet:absolute mobile:max-tablet:bottom-0 mobile:max-tablet:left-0',
    switches.tablet &&
      keys.tablet === CELL_BADGE_POSITION.BOTTOM_LEFT &&
      'tablet:max-desktop:absolute tablet:max-desktop:bottom-0 tablet:max-desktop:left-0',
    switches.desktop &&
      keys.desktop === CELL_BADGE_POSITION.BOTTOM_LEFT &&
      'desktop:max-large-desktop:absolute desktop:max-large-desktop:bottom-0 desktop:max-large-desktop:left-0',
    switches['large-desktop'] &&
      keys['large-desktop'] === CELL_BADGE_POSITION.BOTTOM_LEFT &&
      'large-desktop:absolute large-desktop:bottom-0 large-desktop:left-0',
    switches.mobile &&
      keys.mobile === CELL_BADGE_POSITION.BOTTOM_RIGHT &&
      'mobile:max-tablet:absolute mobile:max-tablet:bottom-0 mobile:max-tablet:right-0',
    switches.tablet &&
      keys.tablet === CELL_BADGE_POSITION.BOTTOM_RIGHT &&
      'tablet:max-desktop:absolute tablet:max-desktop:bottom-0 tablet:max-desktop:right-0',
    switches.desktop &&
      keys.desktop === CELL_BADGE_POSITION.BOTTOM_RIGHT &&
      'desktop:max-large-desktop:absolute desktop:max-large-desktop:bottom-0 desktop:max-large-desktop:right-0',
    switches['large-desktop'] &&
      keys['large-desktop'] === CELL_BADGE_POSITION.BOTTOM_RIGHT &&
      'large-desktop:absolute large-desktop:bottom-0 large-desktop:right-0',
    switches.mobile &&
      keys.mobile === CELL_BADGE_POSITION.CENTER &&
      'mobile:max-tablet:absolute mobile:max-tablet:left-1/2 mobile:max-tablet:top-1/2 mobile:max-tablet:-translate-x-1/2 mobile:max-tablet:-translate-y-1/2',
    switches.tablet &&
      keys.tablet === CELL_BADGE_POSITION.CENTER &&
      'tablet:max-desktop:absolute tablet:max-desktop:left-1/2 tablet:max-desktop:top-1/2 tablet:max-desktop:-translate-x-1/2 tablet:max-desktop:-translate-y-1/2',
    switches.desktop &&
      keys.desktop === CELL_BADGE_POSITION.CENTER &&
      'desktop:max-large-desktop:absolute desktop:max-large-desktop:left-1/2 desktop:max-large-desktop:top-1/2 desktop:max-large-desktop:-translate-x-1/2 desktop:max-large-desktop:-translate-y-1/2',
    switches['large-desktop'] &&
      keys['large-desktop'] === CELL_BADGE_POSITION.CENTER &&
      'large-desktop:absolute large-desktop:left-1/2 large-desktop:top-1/2 large-desktop:-translate-x-1/2 large-desktop:-translate-y-1/2'
  );
}

/**
 * Visibility, overlay position, and all four Cell Badging margins.
 */
function getCellBadgeWrapperClassName({
  assets,
  switches,
}: {
  assets: any;
  switches: ResponsiveBooleans;
}): string {
  return getCellVisibillityClasses(
    switches,
    twMerge(
      'z-[7] flex items-center mt-cell-badge-t mr-cell-badge-r mb-cell-badge-b ml-cell-badge-l',
      getCellBadgePositionClasses({ assets, switches })
    )
  );
}

/**
 * Group-hover classes that hide the resting badge when a hover badge exists.
 */
function getCellHoverBadgeHiddenGroupClasses(
  cellHoverBadgeSwitch: ResponsiveSources
) {
  return twMerge(
    cellHoverBadgeSwitch.mobile &&
      `mobile:max-tablet:group-hover-custom:hidden`,
    cellHoverBadgeSwitch.tablet &&
      `tablet:max-desktop:group-hover-custom:hidden`,
    cellHoverBadgeSwitch.desktop &&
      `desktop:max-large-desktop:group-hover-custom:hidden`,
    cellHoverBadgeSwitch['large-desktop'] &&
      `large-desktop:group-hover-custom:hidden`
  );
}

/**
 * Group-hover classes that show the hover badge.
 */
function getCellHoverBadgeBlockGroupClasses(
  cellHoverBadgeSwitch: ResponsiveSources
) {
  return twMerge(
    cellHoverBadgeSwitch.mobile && `mobile:max-tablet:group-hover-custom:block`,
    cellHoverBadgeSwitch.tablet &&
      `tablet:max-desktop:group-hover-custom:block`,
    cellHoverBadgeSwitch.desktop &&
      `desktop:max-large-desktop:group-hover-custom:block`,
    cellHoverBadgeSwitch['large-desktop'] &&
      `large-desktop:group-hover-custom:block`
  );
}

type CellBadgeData = {
  cellBadgeSwitches: ResponsiveBooleans;
  cellBadgeImageSources: ResponsiveSources;
  cellHoverBadgeSwitches: ResponsiveBooleans;
  cellHoverBadgeImageSources: ResponsiveSources;
  cellSelectedBadgeSwitches: ResponsiveBooleans;
  cellSelectedBadgeImageSources: ResponsiveSources;
  cellSelectedAndHoverBadgeSwitches: ResponsiveBooleans;
  cellSelectedAndHoverBadgeImageSources: ResponsiveSources;
  componentVisibilityClasses: ResponsiveBooleans;
};

/**
 * Reads default, hover, selected, and selected&hover badge config per breakpoint.
 */
function getCellBadgeData(assets: any): CellBadgeData {
  const cellBadgeSwitch: ResponsiveSources = getCellBreakpointValues({
    config: assets,
    key: 'cell_badge_switch',
  });

  const cellBadgeImageSources: ResponsiveSources = getCellBreakpointValues({
    config: assets,
    key: 'cell_badge',
  });

  const cellBadgeSwitches: ResponsiveBooleans = {
    mobile: !!(cellBadgeSwitch.mobile && cellBadgeImageSources.mobile),
    tablet: !!(cellBadgeSwitch.tablet && cellBadgeImageSources.tablet),
    desktop: !!(cellBadgeSwitch.desktop && cellBadgeImageSources.desktop),
    'large-desktop': !!(
      cellBadgeSwitch['large-desktop'] && cellBadgeImageSources['large-desktop']
    ),
  };

  const cellHoverBadgeSwitch: ResponsiveSources = getCellBreakpointValues({
    config: assets,
    key: 'cell_hover_badge_switch',
  });

  const cellHoverBadgeImageSources: ResponsiveSources = getCellBreakpointValues(
    {
      config: assets,
      key: 'cell_hover_badge',
    }
  );

  const cellHoverBadgeSwitches: ResponsiveBooleans = {
    mobile: !!(
      cellHoverBadgeSwitch.mobile && cellHoverBadgeImageSources.mobile
    ),
    tablet: !!(
      cellHoverBadgeSwitch.tablet && cellHoverBadgeImageSources.tablet
    ),
    desktop: !!(
      cellHoverBadgeSwitch.desktop && cellHoverBadgeImageSources.desktop
    ),
    'large-desktop': !!(
      cellHoverBadgeSwitch['large-desktop'] &&
      cellHoverBadgeImageSources['large-desktop']
    ),
  };

  const cellSelectedBadgeSwitch: ResponsiveSources = getCellBreakpointValues({
    config: assets,
    key: 'cell_selected_badge_switch',
  });

  const cellSelectedBadgeImageSources: ResponsiveSources =
    getCellBreakpointValues({
      config: assets,
      key: 'selected_cell_badge',
    });

  const cellSelectedBadgeSwitches: ResponsiveBooleans = {
    mobile: !!(
      cellSelectedBadgeSwitch.mobile && cellSelectedBadgeImageSources.mobile
    ),
    tablet: !!(
      cellSelectedBadgeSwitch.tablet && cellSelectedBadgeImageSources.tablet
    ),
    desktop: !!(
      cellSelectedBadgeSwitch.desktop && cellSelectedBadgeImageSources.desktop
    ),
    'large-desktop': !!(
      cellSelectedBadgeSwitch['large-desktop'] &&
      cellSelectedBadgeImageSources['large-desktop']
    ),
  };

  const cellSelectedAndHoverBadgeSwitch: ResponsiveSources =
    getCellBreakpointValues({
      config: assets,
      key: 'cell_selected_and_hover_badge_switch',
    });

  const cellSelectedAndHoverBadgeImageSources: ResponsiveSources =
    getCellBreakpointValues({
      config: assets,
      key: 'cell_selected_and_hover_badge',
    });

  const cellSelectedAndHoverBadgeSwitches: ResponsiveBooleans = {
    mobile: !!(
      cellSelectedAndHoverBadgeSwitch.mobile &&
      cellSelectedAndHoverBadgeImageSources.mobile
    ),
    tablet: !!(
      cellSelectedAndHoverBadgeSwitch.tablet &&
      cellSelectedAndHoverBadgeImageSources.tablet
    ),
    desktop: !!(
      cellSelectedAndHoverBadgeSwitch.desktop &&
      cellSelectedAndHoverBadgeImageSources.desktop
    ),
    'large-desktop': !!(
      cellSelectedAndHoverBadgeSwitch['large-desktop'] &&
      cellSelectedAndHoverBadgeImageSources['large-desktop']
    ),
  };

  const componentVisibilityClasses = {
    mobile:
      cellBadgeSwitches.mobile ||
      cellHoverBadgeSwitches.mobile ||
      cellSelectedBadgeSwitches.mobile ||
      cellSelectedAndHoverBadgeSwitches.mobile,
    tablet:
      cellBadgeSwitches.tablet ||
      cellHoverBadgeSwitches.tablet ||
      cellSelectedBadgeSwitches.tablet ||
      cellSelectedAndHoverBadgeSwitches.tablet,
    desktop:
      cellBadgeSwitches.desktop ||
      cellHoverBadgeSwitches.desktop ||
      cellSelectedBadgeSwitches.desktop ||
      cellSelectedAndHoverBadgeSwitches.desktop,
    'large-desktop':
      cellBadgeSwitches['large-desktop'] ||
      cellHoverBadgeSwitches['large-desktop'] ||
      cellSelectedBadgeSwitches['large-desktop'] ||
      cellSelectedAndHoverBadgeSwitches['large-desktop'],
  };

  return {
    cellBadgeSwitches,
    cellBadgeImageSources,
    cellHoverBadgeSwitches,
    cellHoverBadgeImageSources,
    cellSelectedBadgeSwitches,
    cellSelectedBadgeImageSources,
    cellSelectedAndHoverBadgeSwitches,
    cellSelectedAndHoverBadgeImageSources,
    componentVisibilityClasses,
  };
}
